import { Notification } from "../interfaces/notifications";
import * as moment from "moment";
import { formatUtcTimestampLocal, parseUtcTimestamp } from "./utc-timestamp";
import { tr } from "./tr";

export type NotificationCategory =
  | "cancellation"
  | "booking"
  | "gift"
  | "self_order"
  | "order";

export type NotificationEntityType = "reservation" | "gift" | "self_order" | "order";

export interface NotificationDisplayMarkers {
  invoiceRequested?: boolean;
  shippingRequested?: boolean;
}

export interface NotificationDisplay {
  category: NotificationCategory;
  subtype?: string;
  title: string;
  body: string;
  entityType?: NotificationEntityType;
  entityId?: string;
  markers?: NotificationDisplayMarkers;
  timezone?: string;
  receivedAtUtc?: string;
  standaloneInvoiceRequest?: boolean;
}

const ND = "N/D";

/** Enable with `window.__DEBUG_LEGACY_NOTIFICATIONS__ = true` in the console. */
function isLegacyNotificationDebugEnabled(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  const debugWindow = window as Window & { __DEBUG_LEGACY_NOTIFICATIONS__?: boolean };
  return debugWindow.__DEBUG_LEGACY_NOTIFICATIONS__ === true;
}

function debugLegacyNotification(message: string, data?: Record<string, unknown>): void {
  if (!isLegacyNotificationDebugEnabled()) {
    return;
  }
  if (data) {
    console.log(`[legacy-notification] ${message}`, data);
    return;
  }
  console.log(`[legacy-notification] ${message}`);
}

const UUID_IN_TITLE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/** N-1: rewrite legacy campanella titles that embedded orders.code (UUID) instead of WS id. */
function sanitizeEcommerceOrderTitle(title: string, entityId?: string): string {
  const id = entityId != null ? String(entityId).trim() : "";
  if (!id || !/^\d+$/.test(id) || !title || !UUID_IN_TITLE.test(title)) {
    return title;
  }
  return title.replace(UUID_IN_TITLE, id);
}

export function getNotificationDisplay(notification: Notification): NotificationDisplay | null {
  const display = notification?.payload?.display;
  if (display?.title && display?.body) {
    const mapped = { ...(display as NotificationDisplay) };
    mapped.title = sanitizeEcommerceOrderTitle(mapped.title, mapped.entityId);
    if (isLegacyNotificationDebugEnabled()) {
      const title = mapped.title || notification.title || "";
      const body = mapped.body || notification.message || "";
      const mentionsGift = notificationMentionsGift(title, body, notification.event_name || "");
      const mentionsSelfOrder = notificationMentionsSelfOrder(title, body, notification.event_name || "");
      if (mentionsGift || mentionsSelfOrder) {
        debugLegacyNotification("using payload.display (legacy mapper skipped)", {
          id: notification.id,
          event_name: notification.event_name,
          displayCategory: mapped.category,
          displayEntityType: mapped.entityType,
          title,
          body,
          mentionsGift,
          mentionsSelfOrder,
        });
      }
    }
    return mapped;
  }
  return mapLegacyNotification(notification);
}

export interface NotificationCategoryTone {
  bar: string;
  iconBackground: string;
  iconColor: string;
  matIcon: string;
}

export function getCategoryTone(category: NotificationCategory | string | undefined): NotificationCategoryTone {
  switch (category) {
    case "cancellation":
      return {
        bar: "#cf3f52",
        iconBackground: "#fdecee",
        iconColor: "#cf3f52",
        matIcon: "highlight_off",
      };
    case "gift":
      return {
        bar: "#7c5ce0",
        iconBackground: "#f1e9fc",
        iconColor: "#7c5ce0",
        matIcon: "redeem",
      };
    case "self_order":
      return {
        bar: "#2f9e66",
        iconBackground: "#e7f6ec",
        iconColor: "#2f9e66",
        matIcon: "shopping_cart",
      };
    case "order":
      return {
        bar: "#b85a69",
        iconBackground: "#f8e8ec",
        iconColor: "#b85a69",
        matIcon: "shopping_bag",
      };
    case "booking":
    default:
      return {
        bar: "#009ade",
        iconBackground: "#e8f0fa",
        iconColor: "#003b71",
        matIcon: "event",
      };
  }
}

export function getCategoryBarColor(category: NotificationCategory | string | undefined): string {
  return getCategoryTone(category).bar;
}

export function formatNotificationListTimestamp(value: string | Date | null | undefined): string {
  const parsed = parseUtcTimestamp(value);
  if (!parsed) {
    return "";
  }
  const now = moment();
  const at = moment(parsed);
  const diffMinutes = now.diff(at, "minutes");

  if (diffMinutes >= 0 && diffMinutes < 60) {
    const mins = Math.max(diffMinutes, 0);
    return tr("{{count}} min ago").replace("{{count}}", String(mins));
  }
  if (at.isSame(now, "day")) {
    return `${tr("Today")} ${at.format("HH:mm")}`;
  }
  return at.format("DD/MM/YYYY HH:mm");
}

export function formatNotificationTooltip(value: string | Date | null | undefined): string {
  const parsed = parseUtcTimestamp(value);
  if (!parsed) {
    return "";
  }
  const at = moment(parsed);
  return tr("Received on {{date}} at {{time}}")
    .replace("{{date}}", at.format("DD/MM/YYYY"))
    .replace("{{time}}", at.format("HH:mm"));
}

export function formatNotificationFallback(value: string | Date | null | undefined): string {
  return formatUtcTimestampLocal(value, "dd/MM/yyyy HH:mm");
}

function notificationMentionsGift(...parts: string[]): boolean {
  return parts.some((part) => part.toLowerCase().includes("gift"));
}

function notificationMentionsSelfOrder(...parts: string[]): boolean {
  return parts.some((part) => {
    const lower = part.toLowerCase();
    return lower.includes("self order") || lower.includes("self_order");
  });
}

function isGiftLegacy(notification: Notification, eventName: string, title: string, body: string): boolean {
  const payloadEventName = (notification.payload?.event?.event_name || "").toLowerCase();
  const giftId = notification.payload?.tokens?.gift?.id || notification.payload?.event?.gift_id;

  return (
    eventName.includes("gift") ||
    payloadEventName.includes("gift") ||
    notificationMentionsGift(title, body) ||
    !!giftId
  );
}

function isSelfOrderLegacy(notification: Notification, eventName: string, title: string, body: string): boolean {
  const payloadEventName = (notification.payload?.event?.event_name || "").toLowerCase();

  return (
    eventName.includes("preorder") ||
    eventName.includes("self_order") ||
    payloadEventName.includes("preorder") ||
    payloadEventName.includes("self_order") ||
    notificationMentionsSelfOrder(title, body) ||
    !!notification.payload?.tokens?.preorder?.id ||
    !!notification.payload?.personalizations?.[0]?.dynamic_template_data?.self_order
  );
}

function isReservationLegacy(notification: Notification, eventName: string): boolean {
  return (
    eventName.includes("reservation") ||
    !!notification.payload?.personalizations?.[0]?.dynamic_template_data?.reservation ||
    !!notification.payload?.tokens?.reservation?.id
  );
}

function mapLegacyNotification(notification: Notification): NotificationDisplay | null {
  const eventName = (notification.event_name || "").toLowerCase();
  const title = notification.title || ND;
  const body = notification.message || ND;
  const mentionsGift = notificationMentionsGift(title, body);
  const mentionsSelfOrder = notificationMentionsSelfOrder(title, body);
  const isGift = isGiftLegacy(notification, eventName, title, body);
  const isSelfOrder = isSelfOrderLegacy(notification, eventName, title, body);
  const isReservation = isReservationLegacy(notification, eventName);
  const giftToken = notification.payload?.tokens?.gift;

  if (isLegacyNotificationDebugEnabled()) {
    debugLegacyNotification("mapping legacy notification", {
      id: notification.id,
      event_name: notification.event_name,
      payload_event_name: notification.payload?.event?.event_name,
      title,
      body,
      checks: {
        isGift,
        isSelfOrder,
        isReservation,
        mentionsGift,
        mentionsSelfOrder,
        hasGiftTokenObject: !!giftToken,
        hasGiftId: !!giftToken?.id,
        hasReservationId: !!notification.payload?.tokens?.reservation?.id,
        hasPreorderId: !!notification.payload?.tokens?.preorder?.id,
      },
      note:
        giftToken && !giftToken.id
          ? "payload.tokens.gift exists but has no id — email template stub, not a gift notification"
          : undefined,
    });
  }

  if (isGift) {
    const result = {
      category: "gift" as const,
      title,
      body,
      entityType: "gift" as const,
      entityId: notification.payload?.tokens?.gift?.id || notification.payload?.event?.gift_id,
      subtype: "legacy",
    };
    debugLegacyNotification("classified as gift", { id: notification.id, result });
    return result;
  }
  if (isSelfOrder) {
    const result = {
      category: "self_order" as const,
      title,
      body,
      entityType: "self_order" as const,
      entityId:
        notification.payload?.tokens?.preorder?.id ||
        notification.payload?.event?.id ||
        notification.payload?.personalizations?.[0]?.dynamic_template_data?.self_order?.id,
      subtype: "legacy",
    };
    debugLegacyNotification("classified as self_order", { id: notification.id, result });
    return result;
  }
  if (isReservation) {
    const category: NotificationCategory =
      eventName.includes("cancel") || eventName.includes("revok") ? "cancellation" : "booking";
    const result: NotificationDisplay = {
      category,
      title,
      body,
      entityType: "reservation",
      entityId: extractReservationId(notification),
      subtype: "legacy",
    };
    debugLegacyNotification("classified as reservation/booking", { id: notification.id, result });
    return result;
  }

  const fallback = {
    category: "booking" as const,
    title,
    body,
    subtype: "legacy",
  };
  debugLegacyNotification("classified as booking (fallback)", { id: notification.id, result: fallback });
  return fallback;
}

function extractReservationId(notification: Notification): string | undefined {
  return (
    notification.payload?.tokens?.reservation?.id ||
    notification.payload?.personalizations?.[0]?.dynamic_template_data?.reservation?.id ||
    notification.payload?.event?.reservation_id
  );
}
